import { useMemo, useState } from "react";
import { Link, useSearch } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  GitCompareArrows,
  Trophy,
  Users,
  Target,
  Banknote,
  Clock,
  Percent,
  MapPin,
  Check,
} from "lucide-react";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}
function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type PeriodKind = "month" | "quarter" | "year";

function useQueryIds(): number[] {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const raw = params.get("ids") ?? "";
  return raw
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0)
    .slice(0, 4);
}

function CrmSellerCompareContent() {
  const userIds = useQueryIds();
  const [kind, setKind] = useState<PeriodKind>("month");

  const sellersList = trpc.crm.sellers.list.useQuery({});

  // Hooks para stats de até 4 vendedores (posições fixas para respeitar regra de hooks).
  const statsA = trpc.crm.sellers.get.useQuery(
    userIds[0] ? { userId: userIds[0], period: { kind } } : (undefined as any),
    { enabled: !!userIds[0] },
  );
  const statsB = trpc.crm.sellers.get.useQuery(
    userIds[1] ? { userId: userIds[1], period: { kind } } : (undefined as any),
    { enabled: !!userIds[1] },
  );
  const statsC = trpc.crm.sellers.get.useQuery(
    userIds[2] ? { userId: userIds[2], period: { kind } } : (undefined as any),
    { enabled: !!userIds[2] },
  );
  const statsD = trpc.crm.sellers.get.useQuery(
    userIds[3] ? { userId: userIds[3], period: { kind } } : (undefined as any),
    { enabled: !!userIds[3] },
  );
  const statsByPos = [statsA, statsB, statsC, statsD];

  const ordered = useMemo(() => {
    const list = sellersList.data ?? [];
    return userIds.map((id, idx) => {
      const row = list.find((r: any) => r.profile.userId === id);
      const stats = statsByPos[idx]?.data?.stats;
      return row ? { ...row, stats } : null;
    });
  }, [userIds, sellersList.data, statsByPos]);

  const highlights = useMemo(() => {
    let bestRevenue = -1;
    let bestRevenueId: number | null = null;
    let bestWin = -1;
    let bestWinId: number | null = null;
    let bestAchievement = -1;
    let bestAchievementId: number | null = null;
    let fastestCycle = Infinity;
    let fastestCycleId: number | null = null;
    ordered.forEach((row) => {
      if (!row || !row.stats) return;
      const s = row.stats;
      if (s.wonRevenue > bestRevenue) {
        bestRevenue = s.wonRevenue;
        bestRevenueId = row.profile.userId;
      }
      if (s.winRate !== null && s.winRate > bestWin) {
        bestWin = s.winRate;
        bestWinId = row.profile.userId;
      }
      if (s.goalAchievement !== null && s.goalAchievement > bestAchievement) {
        bestAchievement = s.goalAchievement;
        bestAchievementId = row.profile.userId;
      }
      if (
        s.avgCycleDays !== null &&
        s.avgCycleDays > 0 &&
        s.avgCycleDays < fastestCycle
      ) {
        fastestCycle = s.avgCycleDays;
        fastestCycleId = row.profile.userId;
      }
    });
    return {
      bestRevenueId,
      bestWinId,
      bestAchievementId,
      fastestCycleId,
    };
  }, [ordered]);

  if (userIds.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <GitCompareArrows className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <h3 className="font-semibold mb-1">Nada para comparar</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Selecione 2 a 4 vendedores no ranking para comparar.
          </p>
          <Link href="/crm/sellers">
            <Button>
              <Users className="h-4 w-4 mr-1.5" />
              Abrir ranking
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <GitCompareArrows className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">
              Comparar vendedores
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              {userIds.length} selecionado{userIds.length > 1 ? "s" : ""} · lado a lado
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 p-1 bg-muted/60 rounded-lg">
            {(["month", "quarter", "year"] as PeriodKind[]).map((k) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${
                  kind === k
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {k === "month" ? "Mês" : k === "quarter" ? "Trimestre" : "Ano"}
              </button>
            ))}
          </div>
          <Link href="/crm/sellers">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Voltar
            </Button>
          </Link>
        </div>
      </div>

      {sellersList.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: userIds.length }).map((_, i) => (
            <Skeleton key={i} className="h-96 w-full" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div
            className="grid gap-3 min-w-[640px]"
            style={{
              gridTemplateColumns: `160px repeat(${userIds.length}, minmax(220px, 1fr))`,
            }}
          >
            {/* ── Header row ─────────────────── */}
            <div />
            {ordered.map((row: any, idx) => {
              if (!row) return <div key={idx}>—</div>;
              const territories = (row.profile.territories as string[]) ?? [];
              return (
                <Link
                  key={row.profile.id}
                  href={`/crm/sellers/${row.profile.userId}`}
                  className="rounded-lg border bg-card p-3 hover:shadow-md transition"
                >
                  <div className="flex items-center gap-2">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {initials(row.user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {row.user?.name ?? row.user?.email ?? "?"}
                      </div>
                      {row.user?.jobTitle && (
                        <div className="text-[11px] text-muted-foreground truncate">
                          {row.user.jobTitle}
                        </div>
                      )}
                    </div>
                  </div>
                  {territories.length > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground flex-wrap">
                      <MapPin className="h-3 w-3" />
                      {territories.slice(0, 4).join(", ")}
                    </div>
                  )}
                </Link>
              );
            })}

            {/* ── Meta mensal ──────────────── */}
            <RowLabel icon={Target} label="Meta mensal" />
            {ordered.map((row: any, idx) => (
              <Cell
                key={idx}
                value={
                  <span className="text-lg font-bold tabular-nums">
                    {formatBRL(row?.profile.monthlyGoal ?? 0)}
                  </span>
                }
              />
            ))}

            {/* ── Realizado ────────────────── */}
            <RowLabel icon={Banknote} label="Realizado" />
            {ordered.map((row: any, idx) => (
              <Cell
                key={idx}
                highlight={highlights.bestRevenueId === row?.profile.userId}
                highlightLabel="Maior receita"
                value={
                  <span className="text-lg font-bold tabular-nums">
                    {formatBRL(row?.stats?.wonRevenue ?? 0)}
                  </span>
                }
              />
            ))}

            {/* ── Progresso meta ───────────── */}
            <RowLabel icon={Target} label="% meta" />
            {ordered.map((row: any, idx) => {
              const achievement = row?.stats?.goalAchievement;
              return (
                <Cell
                  key={idx}
                  highlight={
                    highlights.bestAchievementId === row?.profile.userId &&
                    achievement !== null
                  }
                  highlightLabel="Maior atingimento"
                  value={
                    achievement === null || achievement === undefined ? (
                      <span className="text-muted-foreground">sem meta</span>
                    ) : (
                      <div className="space-y-1">
                        <span className="text-lg font-bold tabular-nums">
                          {achievement.toFixed(0)}%
                        </span>
                        <Progress
                          value={Math.min(100, achievement)}
                          className={
                            achievement >= 100 ? "[&>div]:bg-emerald-500" : ""
                          }
                        />
                      </div>
                    )
                  }
                />
              );
            })}

            {/* ── Fechados / Perdidos ───────── */}
            <RowLabel icon={Trophy} label="Fechados / Perdidos" />
            {ordered.map((row: any, idx) => (
              <Cell
                key={idx}
                value={
                  <span className="text-lg font-semibold tabular-nums">
                    <span className="text-emerald-600">
                      {row?.stats?.wonCount ?? 0}
                    </span>
                    <span className="text-muted-foreground mx-1">/</span>
                    <span className="text-red-600">
                      {row?.stats?.lostCount ?? 0}
                    </span>
                  </span>
                }
              />
            ))}

            {/* ── Win rate ─────────────────── */}
            <RowLabel icon={Trophy} label="Win rate" />
            {ordered.map((row: any, idx) => {
              const wr = row?.stats?.winRate;
              return (
                <Cell
                  key={idx}
                  highlight={
                    highlights.bestWinId === row?.profile.userId && wr !== null
                  }
                  highlightLabel="Melhor conversão"
                  value={
                    <span className="text-lg font-bold tabular-nums">
                      {wr === null || wr === undefined ? "—" : `${wr.toFixed(0)}%`}
                    </span>
                  }
                />
              );
            })}

            {/* ── Ticket médio ─────────────── */}
            <RowLabel icon={Banknote} label="Ticket médio" />
            {ordered.map((row: any, idx) => (
              <Cell
                key={idx}
                value={
                  <span className="text-base font-semibold tabular-nums">
                    {formatCompact(row?.stats?.avgTicket ?? 0)}
                  </span>
                }
              />
            ))}

            {/* ── Ciclo médio ──────────────── */}
            <RowLabel icon={Clock} label="Ciclo médio" />
            {ordered.map((row: any, idx) => {
              const cycle = row?.stats?.avgCycleDays;
              return (
                <Cell
                  key={idx}
                  highlight={
                    highlights.fastestCycleId === row?.profile.userId &&
                    cycle !== null
                  }
                  highlightLabel="Mais rápido"
                  value={
                    <span className="text-base font-semibold tabular-nums">
                      {cycle === null || cycle === undefined ? "—" : `${cycle}d`}
                    </span>
                  }
                />
              );
            })}

            {/* ── Em aberto ────────────────── */}
            <RowLabel icon={Users} label="Em aberto" />
            {ordered.map((row: any, idx) => (
              <Cell
                key={idx}
                value={
                  <span className="text-base font-semibold tabular-nums">
                    {row?.stats?.openCount ?? 0}
                  </span>
                }
              />
            ))}

            {/* ── Comissão ─────────────────── */}
            <RowLabel icon={Percent} label="Comissão" />
            {ordered.map((row: any, idx) => (
              <Cell
                key={idx}
                value={
                  <span className="text-base font-semibold tabular-nums">
                    {Number(row?.profile.commissionPct ?? 0).toFixed(2)}%
                  </span>
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RowLabel({ icon: Icon, label }: { icon?: any; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-t pt-3 first:border-t-0 first:pt-0">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label}
    </div>
  );
}

function Cell({
  value,
  highlight = false,
  highlightLabel,
}: {
  value: React.ReactNode;
  highlight?: boolean;
  highlightLabel?: string;
}) {
  return (
    <div
      className={`border-t pt-3 first:border-t-0 first:pt-0 relative ${
        highlight
          ? "bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-3 py-2 border border-emerald-200 dark:border-emerald-900"
          : ""
      }`}
    >
      {value}
      {highlight && highlightLabel && (
        <Badge className="bg-emerald-600 absolute -top-2 right-2 text-[9px] h-4">
          <Check className="h-2.5 w-2.5 mr-0.5" />
          {highlightLabel}
        </Badge>
      )}
    </div>
  );
}

export default function CrmSellerCompare() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:sellers_view">
        <CrmSellerCompareContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
